import { useEffect } from "react";
import * as signalR from "@microsoft/signalr";
import { useQueryClient } from "@tanstack/react-query";
import type { NotificationItem } from "@/shared/types/Notifications";
import { NOTIFICATIONS_QUERY_KEY } from "./useNotifications";
import { ENV_CONFIG } from "@/config/env.example";
import { authService } from "@/services/Auth/auth.service";
import { logger } from "@/lib/logger";

interface NotificationPayload {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead?: boolean;
  createdAt?: string;
}

export const useNotificationsHub = (enable = true) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enable) return;
    const accessToken = authService.getToken();
    
    if (!accessToken) {
      logger.warn("No access token available for SignalR connection");
      return;
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(ENV_CONFIG.NOTIFICATIONS_HUB_URL, {
        accessTokenFactory: () => accessToken,
      })   
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.elapsedMilliseconds < 60000) {
            return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
          }
          return null;
        }
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on("notificationReceived", (payload: NotificationPayload) => {
      const newNotification: NotificationItem = {
        id: payload.id,
        type: payload.type,
        title: payload.title,
        message: payload.message,
        isRead: payload.isRead ?? false,
        createdAt: payload.createdAt ?? new Date().toISOString(),
      };

      queryClient.setQueryData<NotificationItem[]>(
        NOTIFICATIONS_QUERY_KEY,
        (prev) => [newNotification, ...(prev ?? [])]
      );
    });

    connection.onclose((error) => {
      if (error) {
        logger.error("SignalR connection closed with error:", error);
      } else {
        logger.info("SignalR connection closed");
      }
    });

    connection.onreconnecting((error) => {
      logger.warn("SignalR reconnecting...", error);
    });

    connection.onreconnected((connectionId) => {
      logger.info("SignalR reconnected", connectionId);
    });

    connection.start().catch((err) => {
      logger.error("Error connecting to notifications hub", err);
    });

    return () => {
      connection.stop().catch((err) => {
        logger.error("Error stopping SignalR connection", err);
      });
    };
  }, [queryClient]);
};
